
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Company, UserRole, User } from '../../types';
import { Card, Button, Input, Select, Badge } from '../UI';
import { useTitle, PAGE_TITLES } from '../../hooks';
import { ROUTES } from '../../routes';
import { createCompanySlug } from './CompanyDetail';
import { 
  Plus, 
  MoreVertical, 
  Briefcase, 
  Filter, 
  CornerDownRight, 
  Building, 
  Settings, 
  Trash2,
  Landmark,
  Search,
  X
} from 'lucide-react';

interface CompanyManagerProps {
  companies: Company[];
  onAddCompany: (company: Company) => void;
  onUpdateCompany: (company: Company) => void;
  onDeleteCompany: (companyId: string) => void;
  userRole: UserRole;
  users: User[];
}

export const CompanyManager: React.FC<CompanyManagerProps> = ({ 
  companies, 
  onAddCompany, 
  onUpdateCompany,
  onDeleteCompany,
  userRole,
  users
}) => {
  const navigate = useNavigate();
  
  // Page title
  useTitle(PAGE_TITLES.ADMIN_COMPANIES);

  // Add Company State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newCompany, setNewCompany] = useState<Partial<Company>>({ name: '', industry: '', isHolding: false, parentId: '', spreadsheetId: '' });

  // Access Logic
  const canEdit = userRole === UserRole.ADMINISTRATOR || userRole === UserRole.STAF_ADMIN;

  // Filter State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'holding' | 'subsidiary'>('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Filtered companies
  const filteredCompanies = companies.filter(company => {
    // Search filter
    const matchesSearch = searchQuery === '' || 
      company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.industry?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.country?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Type filter
    const matchesType = filterType === 'all' || 
      (filterType === 'holding' && company.isHolding) ||
      (filterType === 'subsidiary' && !company.isHolding);
    
    return matchesSearch && matchesType;
  });

  const activeFiltersCount = (searchQuery ? 1 : 0) + (filterType !== 'all' ? 1 : 0);

  const clearFilters = () => {
    setSearchQuery('');
    setFilterType('all');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newCompany.name && newCompany.industry) {
      onAddCompany({
        id: `c_${Date.now()}`,
        name: newCompany.name,
        industry: newCompany.industry,
        status: 'active',
        isHolding: newCompany.isHolding || false,
        parentId: newCompany.parentId || undefined,
        spreadsheetId: newCompany.spreadsheetId || undefined,
        bankAccounts: []
      });
      setNewCompany({ name: '', industry: '', isHolding: false, parentId: '', spreadsheetId: '' });
      setIsModalOpen(false);
    }
  };

  const navigateToCompany = (company: Company) => {
    const slug = createCompanySlug(company.name);
    navigate(`${ROUTES.SETTINGS_COMPANIES}/${slug}`);
  };

  // --- Sub-Components ---
  const ActionMenu: React.FC<{ company: Company }> = ({ company }) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleDelete = () => {
      if (!canEdit) return;
      const hasChildren = companies.some(c => c.parentId === company.id);
      if (hasChildren) {
        alert("Tidak dapat menghapus Perusahaan Induk yang memiliki anak perusahaan.");
        return;
      }

      if (confirm(`Apakah Anda yakin ingin menghapus ${company.name}?`)) {
        onDeleteCompany(company.id);
      }
    };

    return (
      <div className="relative" ref={menuRef}>
        <button 
          onClick={() => setIsOpen(!isOpen)}
          className="text-gray-400 hover:text-indigo-600 transition-colors p-2 hover:bg-indigo-50 rounded-full focus:outline-none"
        >
          <MoreVertical className="w-5 h-5" />
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 z-50 animate-fade-in-up origin-top-right overflow-hidden">
            <div className="py-1">
              <button
                onClick={() => {
                  navigateToCompany(company);
                  setIsOpen(false);
                }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-indigo-600 transition-colors"
              >
                <Settings className="w-4 h-4 mr-2" />
                Buka Pengaturan
              </button>
              {canEdit && (
                <button
                  onClick={handleDelete}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors border-t border-gray-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Hapus Entitas
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // DEFAULT LIST VIEW
  return (
    <div className="space-y-6">
      {/* Header with Search and Filter */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
             <h2 className="text-lg font-bold text-gray-900">Daftar Perusahaan</h2>
             <p className="text-sm text-gray-500 mt-1">Kelola struktur grup dan entitas bisnis.</p>
          </div>
          <div className="flex space-x-3">
              <Button 
                variant="secondary" 
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className={activeFiltersCount > 0 ? 'ring-2 ring-indigo-500 ring-offset-1' : ''}
              >
                  <Filter className="w-4 h-4 mr-2" /> 
                  Filter
                  {activeFiltersCount > 0 && (
                    <span className="ml-2 bg-indigo-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {activeFiltersCount}
                    </span>
                  )}
              </Button>
              {canEdit && (
                  <Button onClick={() => setIsModalOpen(true)}>
                      <Plus className="w-4 h-4 mr-2" /> Tambah Entitas
                  </Button>
              )}
          </div>
        </div>

        {/* Filter Panel */}
        {isFilterOpen && (
          <div className="pt-4 border-t border-gray-100 animate-fade-in-up">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search Input */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cari nama, industri, atau negara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Type Filter */}
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as 'all' | 'holding' | 'subsidiary')}
                className="px-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
              >
                <option value="all">Semua Tipe</option>
                <option value="holding">Holding Company</option>
                <option value="subsidiary">Anak Perusahaan</option>
              </select>

              {/* Clear Filters */}
              {activeFiltersCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-500 hover:text-indigo-600 transition-colors whitespace-nowrap"
                >
                  Hapus Filter
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Results count */}
      {(searchQuery || filterType !== 'all') && (
        <p className="text-sm text-gray-500">
          Menampilkan {filteredCompanies.length} dari {companies.length} perusahaan
        </p>
      )}

      {/* Company Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {filteredCompanies.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-white rounded-xl border border-gray-200">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">Tidak ada hasil</h3>
            <p className="text-sm text-gray-500 mb-4">Coba ubah kata kunci atau filter pencarian</p>
            <Button variant="secondary" onClick={clearFilters}>
              Hapus Filter
            </Button>
          </div>
        ) : (
          filteredCompanies.map(company => (
            <div key={company.id} className="group bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all duration-200 flex flex-col h-full overflow-hidden">
                <div className="p-4 sm:p-6 flex-1">
                    <div className="flex justify-between items-start mb-3 sm:mb-4">
                        {/* Company Logo or Icon */}
                        {company.logoUrl ? (
                            <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg overflow-hidden shadow-sm border border-gray-200">
                                <img 
                                    src={company.logoUrl} 
                                    alt={`${company.name} logo`}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        ) : (
                            <div className={`
                                h-10 w-10 sm:h-12 sm:w-12 rounded-lg flex items-center justify-center text-white font-bold text-base sm:text-lg shadow-sm
                                ${company.isHolding ? 'bg-linear-to-br from-indigo-600 to-purple-600' : 'bg-linear-to-br from-blue-500 to-cyan-600'}
                            `}>
                                {company.isHolding ? <Briefcase className="w-5 h-5 sm:w-6 sm:h-6" /> : company.name.charAt(0)}
                            </div>
                        )}
                        <ActionMenu company={company} />
                    </div>
                    
                    <h3 className="font-bold text-gray-900 text-base sm:text-lg mb-1 truncate" title={company.name}>{company.name}</h3>
                    <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">{company.industry}</p>
                    
                    <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                        <Badge color={company.status === 'active' ? 'green' : 'gray'}>{company.status}</Badge>
                        {company.isHolding && <Badge color="indigo">Holding</Badge>}
                        {company.country && <Badge color="gray">{company.country}</Badge>}
                    </div>

                    <div className="space-y-2 text-sm text-gray-600">
                        {company.parentId && (
                            <div className="flex items-center text-xs bg-gray-50 p-2 rounded">
                                <CornerDownRight className="w-3 h-3 mr-2 text-gray-400 shrink-0" />
                                <span className="truncate">Anak dari: <span className="font-medium">{companies.find(p => p.id === company.parentId)?.name || 'Unknown'}</span></span>
                            </div>
                        )}
                        <div className="flex items-center justify-between text-xs text-gray-500 mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-50">
                             <span className="flex items-center"><Landmark className="w-3 h-3 mr-1" /> {company.bankAccounts?.length || 0} Rekening</span>
                             <span className="hidden sm:inline">ID: {company.id}</span>
                        </div>
                    </div>
                </div>
                
                <div className="px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
                    <button 
                        onClick={() => navigateToCompany(company)}
                        className="text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors touch-target py-1"
                    >
                        Kelola Rekening &rarr;
                    </button>
                </div>
            </div>
          ))
        )}
      </div>

      {/* ADD COMPANY MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-100 flex items-end sm:items-center justify-center sm:p-4">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm" 
            onClick={() => setIsModalOpen(false)} 
          />
          
          {/* Modal Content */}
          <div className="relative bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-xl max-h-[90vh] flex flex-col animate-slideUp sm:animate-scaleIn">
            {/* Mobile drag indicator */}
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 bg-gray-300 rounded-full sm:hidden" />
            
            <div className="px-4 sm:px-6 pt-6 pb-4 shrink-0">
              <div className="flex items-center justify-between mb-6 pt-2 sm:pt-0">
                <h3 className="text-base sm:text-lg leading-6 font-bold text-gray-900">Tambah Entitas Baru</h3>
                <button 
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg touch-target"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="px-4 sm:px-6 overflow-y-auto flex-1">
              <form id="company-form" onSubmit={handleSubmit} className="space-y-4 pb-4">
                  <Input 
                      label="Nama Perusahaan" 
                      value={newCompany.name || ''} 
                      onChange={(e) => setNewCompany({...newCompany, name: e.target.value})}
                      required
                      placeholder="Contoh: PT. Maju Mundur"
                  />
                  <Input 
                      label="Industri" 
                      value={newCompany.industry || ''} 
                      onChange={(e) => setNewCompany({...newCompany, industry: e.target.value})}
                      required
                      placeholder="Contoh: Manufaktur, Ritel"
                  />
                  
                  <div className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <input
                          id="isHolding"
                          type="checkbox"
                          checked={newCompany.isHolding || false}
                          onChange={(e) => setNewCompany({...newCompany, isHolding: e.target.checked, parentId: e.target.checked ? '' : newCompany.parentId})}
                          className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                      />
                      <label htmlFor="isHolding" className="ml-3 block text-sm text-gray-900 font-medium cursor-pointer">
                          Ini adalah Perusahaan Induk (Holding)
                      </label>
                  </div>

                  {!newCompany.isHolding && (
                      <Select
                          label="Perusahaan Induk (Opsional)"
                          value={newCompany.parentId || ''}
                          onChange={(e) => setNewCompany({...newCompany, parentId: e.target.value})}
                      >
                          <option value="">Berdiri Sendiri (Independen)</option>
                          {companies.filter(c => c.isHolding).map(h => (
                              <option key={h.id} value={h.id}>{h.name}</option>
                          ))}
                      </Select>
                  )}
              </form>
            </div>
            <div className="bg-gray-50 px-4 sm:px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 border-t border-gray-100 shrink-0 pb-safe">
              <Button variant="secondary" onClick={() => setIsModalOpen(false)} className="w-full sm:w-auto" fullWidthMobile>
                Batal
              </Button>
              <Button type="submit" form="company-form" className="w-full sm:w-auto" fullWidthMobile>
                Simpan Entitas
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
